import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { OrderLineItemProps, SalesOrderProps, ShipmentProps, CreditCardProps, QuotesProps } from '../types/types';

import { API_ROOT, get, patch, post, remove } from '@partsbadger/utils';
import fileDownload from 'js-file-download';

import { QualityDocumentsProps } from '../types/types';
import { notification } from 'antd';

class SalesOrderStore {
    loading = false;

    order?: SalesOrderProps;
    orders: SalesOrderProps[] = [];
    quotes?: [QuotesProps];

    loading_completed_orders = false;
    completed_orders: SalesOrderProps[] = [];

    loading_open_orders = false;
    open_orders: SalesOrderProps[] = [];

    shipments: ShipmentProps[] = [];
    quality_documents: QualityDocumentsProps[] = [];

    order_items: Array<OrderLineItemProps> = []; // Local Line Items assigned to the order
    creditCard?: CreditCardProps | null = null;

    isTourClosed = false;

    constructor() {
        makeObservable(this, {
            // Observables
            loading: observable,
            order: observable,
            orders: observable,
            loading_completed_orders: observable,
            completed_orders: observable,
            loading_open_orders: observable,
            open_orders: observable,
            shipments: observable,
            quality_documents: observable,
            order_items: observable,
            creditCard: observable,
            quotes: observable,
            isTourClosed: observable,

            // Actions
            setLoading: action,
            getAllOrders: action,
            getOpenOrders: action,
            getCompletedOrders: action,
            getOrderById: action,
            getShipmentsBySalesOrderId: action,
            getQualityDocumentsBySalesOrderId: action,
            requestQualityDocumentUpdate: action,
            getPackListBySalesOrderId: action,
            sendToReview: action,
            setOrderItems: action,
            addOrderItemDraft: action,
            createOrderDraft: action,
            patchOrderDraft: action,
            updateOrderItemDraft: action,
            removeOrderItemDraft: action,
            reOrder: action,
            processPayment: action,
            sendCreditCard: action,
            getSalesOrderLineItems: action,
            threeDApproval: action,
            requestShippingAdjusment: action,
            updateShippingInfo: action,
            setOrder: action,

            // Computed
            areThere2DFilesPending: computed,
            areThere3DFilesPending: computed,
        });

        const getTourStatus = localStorage.getItem('tour_openOrders_closed');

        if (getTourStatus === 'true') {
            runInAction(() => {
                this.isTourClosed = true;
            });
        }
    }

    setCloseTheTour() {
        this.isTourClosed = true;
        localStorage.setItem('tour_openOrders_closed', 'true');
    }

    setLoading(loading: boolean) {
        runInAction(() => {
            this.loading = loading;
        });
    }

    getAllOrders = async (params?: any) => {
        runInAction(() => {
            this.loading = true;
        });

        const data = await get(`/customer/sales-orders/`, {
            params: params,
        }).finally(() => {
            runInAction(() => {
                this.loading = false;
            });
        });

        runInAction(() => {
            this.orders = data.results;
        });

        return data.results;
    };

    getOpenOrders(params = {}) {
        this.loading_open_orders = true;

        get(`/customer/sales-orders/`, {
            params: {
                ...params,
                is_completed: false,
                hide_draft_orders: true,
            },
        })
            .then(data => {
                runInAction(() => {
                    this.open_orders = data.results;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading_open_orders = false;
                });
            });
    }

    getCompletedOrders(params = {}) {
        this.loading_completed_orders = true;

        get(`/customer/sales-orders/`, {
            params: {
                ...params,
                is_completed: true,
                hide_draft_orders: true,
            },
        })
            .then(data => {
                runInAction(() => {
                    this.completed_orders = data.results;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading_completed_orders = false;
                });
            });
    }

    async getOrderById(sales_order_id: number) {
        this.loading = true;

        await get(`/customer/sales-orders/${sales_order_id}/`)
            .then(data => {
                runInAction(() => {
                    this.order_items = data.line_items;
                    this.order = data;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    async getShipmentsBySalesOrderId(sales_order_id: number) {
        this.loading = true;
        await get(`/customer/sales-orders/${sales_order_id}/shipments/`)
            .then(data => {
                runInAction(() => {
                    this.shipments = data.results;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    async getQualityDocumentsBySalesOrderId(sales_order_id: number) {
        try {
            const pageSize = 100;
            let page = 1;
            let count = 0;
            let results: QualityDocumentsProps[] = [];

            do {
                const response = await get(
                    `/customer/sales-orders/${sales_order_id}/quality-documents/?page=${page}&page_size=${pageSize}`
                );

                const pageResults = response.results ?? [];
                if (pageResults.length === 0) {
                    break;
                }

                results = results.concat(pageResults);
                count = response.count ?? results.length;
                page += 1;
            } while (results.length < count);

            this.quality_documents = results;
        } catch (err) {
            this.quality_documents = [];
        }
    }

    async requestQualityDocumentUpdate(
        sales_order_id: number,
        payload: import('../types/types').QualityDocumentUpdateRequestPayload
    ) {
        return post(`/customer/sales-orders/${sales_order_id}/request-quality-document-update/`, payload);
    }

    async downloadQualityDocumentBySalesOrderId(sales_order_id: number, document_id: number) {
        return get(`/customer/sales-orders/${sales_order_id}/quality-documents/${document_id}/download/`, {
            responseType: 'blob',
        });
    }

    async downloadAllQualityDocumentsBySalesOrderId(sales_order_id: number) {
        return get(`/customer/sales-orders/${sales_order_id}/download-all-quality-documents/`, {
            responseType: 'blob',
        });
    }

    async getPackListBySalesOrderId(sales_order_id: number, box_id: number) {
        this.loading = true;
        return get(`/customer/sales-orders/${sales_order_id}/download-packing-slip/${box_id}/`, {
            responseType: 'blob',
        });
    }

    sendToReview(order_id: number, payload: any) {
        this.loading = true;

        return post(`/customer/sales-orders/${order_id}/send-to-review/`, payload)
            .then(data => {
                runInAction(() => {
                    this.order_items = [];
                    this.order = undefined;
                });
                this.quotes = data.quotes;
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    //Credit card Submision store
    async sendCreditCard(payload: CreditCardProps) {
        const formData = new FormData();
        if (payload.amount) {
            formData.append('amount', payload.amount);
        }
        if (payload.customer_email) {
            formData.append('customer_email', payload.customer_email);
        }
        if (payload.notes) {
            formData.append('notes', payload.notes);
        }
        if (payload.representative_email) {
            formData.append('representative_email', payload.representative_email);
        }
        formData.append('ship_cost', payload?.ship_cost);
        formData.append('tax_cost', payload?.tax_cost);
        formData.append('zip_code', payload?.zip_code);
        if (payload.quotes) {
            payload.quotes.forEach(item => {
                formData.append('quotes', String(item));
            });
        }

        const response: any = await post(`/customer/sales-orders/card/`, formData);
        this.creditCard = response;
        return response;
    }

    setOrderItems(order_items: Array<OrderLineItemProps>) {
        this.order_items = order_items;
    }

    addOrderItemDraft(order_id: number, payload: any) {
        this.loading = true;
        post(`/customer/sales-orders/${order_id}/add-item/`, payload)
            .then(response => {
                runInAction(() => {
                    this.order = response;
                    this.order_items = response.line_items;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    createOrderDraft(payload: {
        contact: number;
        quotes?: string[];
        line_items?: {
            name: string;
            quote_has_product?: number;
            quantity: number;
            unit_price: string;
            production_3d_file?: number;
            production_2d_file?: number;
            product_description?: string;
        }[];
    }) {
        this.loading = true;
        return post(`/customer/sales-orders/new/`, payload).finally(() => {
            runInAction(() => {
                this.loading = false;
            });
        });
    }

    patchOrderDraft(order_id: number, payload: any) {
        this.loading = true;
        return patch(`/customer/sales-orders/${order_id}/`, payload)
            .then(response => {
                runInAction(() => {
                    this.order = response;
                    // this.order_items = response.line_items;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    async update_sales_order_field<K extends keyof SalesOrderProps, V extends SalesOrderProps[K]>(key: K, value: V) {
        if (this.order) {
            this.order = {
                ...this.order,
                [key]: value,
            };
            const payload = {
                [key]: value,
            };
            await patch(`/customer/sales-orders/${this.order.id}/`, payload).finally(() => {
                this.loading = false;
            });
        }
    }

    updateOrderItemDraft(order_id: number, line_item_id: number, payload: any) {
        this.loading = true;
        // update in local
        const index = this.order_items.findIndex(item => item.id === line_item_id);
        if (index > -1) {
            this.order_items[index] = {
                ...this.order_items[index],
                ...payload,
            };
        }
        return patch(`/customer/sales-orders/${order_id}/update-item/${line_item_id}/`, payload)
            .then(response => {
                runInAction(() => {
                    this.order = response;
                    this.order_items = response.line_items;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    removeOrderItemDraft(order_id: number, order_item_id: number) {
        this.order_items = this.order_items.filter(current => order_item_id !== current.id);
        remove(`/customer/sales-orders/${order_id}/remove-item/${order_item_id}/`)
            .then(response => {
                runInAction(() => {
                    this.order = response;
                });
            })
            .finally(() => this.setLoading(false));
    }

    reOrder(order_id: number, payload: any) {
        this.loading = true;

        return post(`/customer/sales-orders/${order_id}/reorder/`, payload)
            .then(() => {
                runInAction(() => {
                    this.order_items = [];
                    this.order = undefined;
                });
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    async processPayment(order_id: number, sourceIdToken: string) {
        this.loading = true;

        await post(`/customer/sales-orders/${order_id}/process-payment/`, {
            source_id: sourceIdToken,
        }).finally(() => {
            runInAction(() => {
                this.loading = false;
            });
        });
    }

    getSalesOrderLineItems = async (params: any) => {
        runInAction(() => {
            this.loading = true;
        });

        return await get(`/customer/sales-order-line-items/`, { params: params }).finally(() => {
            runInAction(() => {
                this.loading = false;
            });
        });
    };

    async downloadPacketDocs(url: string, name_file: string) {
        notification.info({
            message: 'Generating file',
            description: 'Be patient, it may take a few seconds.',
        });
        const pdf = await get(url, {
            responseType: 'blob',
        });
        const bytes = await pdf.blob();
        fileDownload(bytes, name_file);
        notification.info({
            message: 'File downloaded',
            description: 'The PDF file has been downloaded successfully.',
        });
    }

    /**
     * Poll the backend for document packet generation status.
     */
    async pollDocumentPacketStatus(
        orderId: number,
        taskId: string,
        onProgress?: (progress: { percent: number; step: string; current: number; total: number }) => void,
        interval = 3000,
        timeout = 600000
    ): Promise<{ file_url: string; file_name: string }> {
        const taskStatusEndpoint = `/staff/sales-orders/${orderId}/get-document-pack-status/?task_id=${taskId}`;
        const startTime = Date.now();

        const checkTaskStatus = async (): Promise<{ file_url: string; file_name: string }> => {
            if (Date.now() - startTime > timeout) {
                throw new Error('Document packet generation timed out. Please try again.');
            }

            const response = await get(taskStatusEndpoint);

            if (response.status === 'PROGRESS' && onProgress) {
                onProgress({
                    percent: response.percent || 0,
                    step: response.step || '',
                    current: response.current || 0,
                    total: response.total || 0,
                });
            }

            if (response.status === 'SUCCESS') {
                return {
                    file_url: response.file_url,
                    file_name: response.file_name,
                };
            } else if (response.status === 'FAILURE') {
                throw new Error('Document packet generation failed: ' + (response.error || 'Unknown error'));
            } else {
                await new Promise(resolve => setTimeout(resolve, interval));
                return checkTaskStatus();
            }
        };

        return checkTaskStatus();
    }

    /**
     * Download document packet using async generation with polling.
     */
    async downloadDocumentPacket(
        orderId: number,
        outtakeId?: number,
        onProgress?: (progress: { percent: number; step: string; current: number; total: number }) => void
    ) {
        try {
            // Use POST so the (potentially large) products payload built
            // server-side is not appended to the URL.
            const startUrl = outtakeId
                ? `/staff/sales-orders/${orderId}/download-document-pack/?outtake=${outtakeId}`
                : `/staff/sales-orders/${orderId}/download-document-pack/`;

            const { task_id: taskId } = await post(startUrl);

            if (!taskId) {
                throw new Error('Failed to start document packet generation. Task ID is missing.');
            }

            notification.info({
                message: 'Generating Document Packet',
                description: 'This may take a few minutes for large orders...',
            });

            const { file_url: fileUrl, file_name: fileName } = await this.pollDocumentPacketStatus(
                orderId,
                taskId,
                onProgress
            );

            const response = await fetch(fileUrl);
            if (!response.ok) {
                throw new Error('Failed to download the generated document packet.');
            }
            const blob = await response.blob();
            fileDownload(blob, fileName);

            notification.success({
                message: 'Downloaded',
                description: 'Document packet downloaded successfully.',
            });
        } catch (error: any) {
            notification.error({
                message: 'Download Failed',
                description: error.message || 'Unknown error',
            });
            throw error;
        }
    }

    async threeDApproval(order_id: number, payload: any) {
        this.loading = true;
        // update in local
        // const index = this.order_items.findIndex(item => item.id === line_item_id);

        // if (index > -1) {
        //     this.order_items[index] = {
        //         ...this.order_items[index],
        //         ...payload,
        //     };
        // }
        return post(`/customer/sales-orders/${order_id}/approve-files/`, payload)
            .then(response => {
                this.order = response;
                this.order_items = response.line_items;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    async requestShippingAdjusment(order_id: number, payload: any) {
        this.loading = true;

        return post(`/customer/sales-orders/${order_id}/shipping-adjustments/`, payload)
            .then(response => {
                // this.order = response;
                // this.order_items = response.line_items;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    async updateShippingInfo(order_id: number, payload: any) {
        this.loading = true;

        return post(`/customer/sales-orders/${order_id}/shipping-info/`, payload)
            .then(response => {
                this.order = response;
                this.order_items = response.line_items;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    async requestBatchScheduleAdjustment(order_id: number, payload: any) {
        this.loading = true;

        return post(`/customer/sales-orders/${order_id}/batch-management-request/`, payload)
            .then(response => {
                // this.order = response;
                // this.order_items = response.line_items;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    async requestOrderAdjustment(order_id: number, payload: any) {
        this.loading = true;

        return post(`/customer/sales-orders/${order_id}/order-adjustment/`, payload)
            .then(response => {
                // this.order = response;
                // this.order_items = response.line_items;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    async setOrder(order: SalesOrderProps) {
        this.order = order;
    }

    get areThere2DFilesPending() {
        return this.order_items.filter(item => !item.production_2d_file && item.quote_has_product).length > 0;
    }

    get areThere3DFilesPending() {
        return this.order_items.filter(item => !item.production_3d_file && item.quote_has_product).length > 0;
    }
}

export default new SalesOrderStore();
